
import React from 'react';
import { Button } from './button';
import { Play, CheckCircle } from 'lucide-react';
import { useTutorial } from '@/hooks/use-tutorial';

interface TutorialTriggerProps {
  tutorialId: string;
  variant?: 'button' | 'icon' | 'link';
  size?: 'sm' | 'lg' | 'default';
  children?: React.ReactNode;
}

export function TutorialTrigger({ 
  tutorialId, 
  variant = 'button', 
  size = 'sm',
  children 
}: TutorialTriggerProps) {
  const { startTutorial, completedTutorials, availableTutorials } = useTutorial();
  
  const tutorial = availableTutorials.find(t => t.id === tutorialId);
  const isCompleted = completedTutorials.includes(tutorialId);
  
  if (!tutorial) {
    console.error('Tutorial not found:', tutorialId);
    return null;
  }

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Tutorial trigger clicked:', tutorialId);
    startTutorial(tutorialId);
  };

  if (variant === 'icon') {
    return (
      <button
        onClick={handleClick}
        className="text-gray-400 hover:text-blue-600 transition-colors p-1"
        title={`Start ${tutorial.name} tutorial`}
      >
        {isCompleted ? (
          <CheckCircle className="h-5 w-5 text-green-600" />
        ) : (
          <Play className="h-5 w-5" />
        )}
      </button>
    );
  }

  if (variant === 'link') {
    return (
      <button
        onClick={handleClick}
        className={`flex items-center gap-2 text-sm font-medium w-full text-left px-3 py-2 rounded-md transition-colors ${
          isCompleted 
            ? 'text-green-600 bg-green-50 hover:bg-green-100' 
            : 'text-blue-600 hover:text-blue-700 hover:bg-blue-50'
        }`}
      >
        <div className="flex-shrink-0">
          {isCompleted ? (
            <CheckCircle className="h-4 w-4" />
          ) : (
            <Play className="h-4 w-4" />
          )}
        </div>
        <span className="flex-1 truncate">
          {children || `Tutorial: ${tutorial.name}`}
        </span>
        {isCompleted && (
          <span className="text-xs text-green-600 font-medium">✓</span>
        )}
      </button>
    );
  }

  return (
    <Button
      variant="outline"
      size={size}
      onClick={handleClick}
      className={`border-blue-200 text-sm ${
        isCompleted 
          ? 'text-green-700 bg-green-50 hover:bg-green-100 border-green-200' 
          : 'text-blue-700 hover:bg-blue-50'
      }`}
    >
      {isCompleted ? (
        <CheckCircle className="h-4 w-4 mr-2" />
      ) : (
        <Play className="h-4 w-4 mr-2" />
      )}
      <span className="truncate">{children || tutorial.name}</span>
      {isCompleted && <span className="ml-2 text-green-600 text-xs">✓</span>}
    </Button>
  );
}
